import { Component, EventEmitter, inject, Input, Output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormSpinnerService, FormSpinnerState } from './form-spinner.service';

/**
 * Full-screen form-submission overlay. Sits on a single soft "aurora" backdrop
 * and morphs between three phases driven by {@link FormSpinnerService}:
 *  - `submitting` — a boxless orbit loader,
 *  - `success`    — an animated tick with a celebratory confetti burst,
 *  - `error`      — a dismissable error result.
 */
@Component({
  selector: 'sml-form-spinner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './form-spinner.component.html',
  styleUrl: './form-spinner.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FormSpinnerComponent {
  @Input() showFormImage?: boolean = false;
  @Input() submittingTitle = 'Submitting your form';
  @Input() submittingMessage = 'Hang tight, this will only take a moment';
  @Input() defaultSuccessTitle = 'Form Complete!';
  @Input() defaultSuccessMessage = 'You will receive an email from SeeMyLegacy with details.';
  @Input() defaultErrorTitle = 'Something went wrong';
  @Input() defaultErrorMessage = 'We couldn’t submit your form. Please try again.';
  @Output() closed = new EventEmitter<void>();

  private readonly formSpinnerService = inject(FormSpinnerService);
  readonly state$ = this.formSpinnerService.getFormSpinnerState();

  private static readonly CONFETTI_COLORS = [
    '#159d53', '#25b46c', '#106b8e', '#4089a5', '#f0b429', '#367bff', '#e0557a',
  ];

  readonly confettiPieces: ConfettiPiece[] = FormSpinnerComponent.buildConfetti(70);
  readonly confettiPiecesBack: ConfettiPiece[] = FormSpinnerComponent.buildConfetti(70);

  private static buildConfetti(count: number): ConfettiPiece[] {
    const colors = FormSpinnerComponent.CONFETTI_COLORS;
    const pieces: ConfettiPiece[] = [];

    for (let i = 0; i < count; i++) {
      const left = (Math.random() * 100).toFixed(2);
      const size = 6 + Math.random() * 6; // 6–12px
      const isCircle = Math.random() < 0.35;
      const height = isCircle ? size : size * (1.2 + Math.random() * 0.8);
      const radius = isCircle ? '50%' : '1px';
      const color = colors[Math.floor(Math.random() * colors.length)];
      const variant = 1 + Math.floor(Math.random() * 6); // 1–6
      const delay = (Math.random() * 1).toFixed(2); // 0–1s
      const duration = (2.6 + Math.random() * 1.8).toFixed(2); // 2.6–4.4s

      pieces.push({
        cls: `sml-fs-cf sml-fs-cf--${variant}`,
        style: {
          left: `${left}%`,
          width: `${size.toFixed(1)}px`,
          height: `${height.toFixed(1)}px`,
          background: color,
          'border-radius': radius,
          'animation-duration': `${duration}s`,
          'animation-delay': `${delay}s`,
        },
      });
    }
    return pieces;
  }

  successTitle(state: FormSpinnerState): string {
    return state.title || this.defaultSuccessTitle;
  }

  successMessage(state: FormSpinnerState): string {
    return state.message || this.defaultSuccessMessage;
  }

  errorTitle(state: FormSpinnerState): string {
    return state.title || this.defaultErrorTitle;
  }

  errorMessage(state: FormSpinnerState): string {
    return state.message || this.defaultErrorMessage;
  }

  onClose(): void {
    this.formSpinnerService.hideFormSpinner();
    this.closed.emit();
  }
}

interface ConfettiPiece {
  cls: string;
  style: Record<string, string>;
}
